import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MagnifyingGlass, CheckCircle, ClockCountdown, FileText, GraduationCap, XCircle, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

import { API } from '@/config/api';

const STATUS_CONFIG = {
  new: { label: 'Received', icon: ClockCountdown, color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', step: 1 },
  under_review: { label: 'Under Review', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', step: 2 },
  documents_requested: { label: 'Documents Requested', icon: FileText, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200', step: 2 },
  exam_prep: { label: 'Exam Preparation', icon: GraduationCap, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200', step: 3 },
  application_submitted: { label: 'Application Submitted', icon: ArrowRight, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', step: 4 },
  admitted: { label: 'Admitted', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50 border-green-200', step: 5 },
  enrolled: { label: 'Enrolled', icon: GraduationCap, color: 'text-green-700', bg: 'bg-green-50 border-green-200', step: 6 },
  rejected: { label: 'Not Successful', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', step: 0 },
};

const STEPS = [
  { num: 1, label: 'Received' },
  { num: 2, label: 'Under Review' },
  { num: 3, label: 'Preparation' },
  { num: 4, label: 'Applied' },
  { num: 5, label: 'Admitted' },
  { num: 6, label: 'Enrolled' },
];

export default function TrackApplication() {
  const [refNumber, setRefNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!refNumber.trim()) { toast.error('Please enter your reference number'); return; }
    setLoading(true);
    setNotFound(false);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/track/${refNumber.trim()}`);
      setResult(data);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = result ? (STATUS_CONFIG[result.status] || STATUS_CONFIG.new) : null;
  const currentStep = statusConfig?.step || 0;

  return (
    <div data-testid="track-application-page">
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Application Tracker</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-4" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Track Your Application
            </h1>
            <p className="text-base lg:text-lg text-gray-600">
              Enter your reference number to check the current status of your application.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          {/* Search Form */}
          <form onSubmit={handleTrack} className="mb-8" data-testid="track-form">
            <div className="flex gap-3">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-gray-500">Reference Number</Label>
                <div className="relative">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={refNumber}
                    onChange={e => setRefNumber(e.target.value.toUpperCase())}
                    placeholder="e.g., SA-260418-0001"
                    className="pl-10 h-12 text-lg font-mono"
                    data-testid="track-ref-input"
                  />
                </div>
              </div>
              <div className="self-end">
                <Button type="submit" disabled={loading} className="bg-blue-700 hover:bg-blue-800 text-white h-12 px-6" data-testid="track-submit-btn">
                  {loading ? 'Searching...' : 'Track'}
                </Button>
              </div>
            </div>
          </form>

          {/* Not Found */}
          {notFound && (
            <div className="text-center py-8 bg-red-50 border border-red-200 rounded-xl" data-testid="track-not-found">
              <XCircle size={40} className="text-red-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>Application Not Found</h3>
              <p className="text-sm text-gray-500">Please check your reference number and try again.</p>
            </div>
          )}

          {/* Result */}
          {result && statusConfig && (
            <div className="space-y-6" data-testid="track-result">
              {/* Status Card */}
              <div className={`rounded-xl border p-6 ${statusConfig.bg}`} data-testid="track-status-card">
                <div className="flex items-center gap-3 mb-3">
                  <statusConfig.icon size={28} weight="fill" className={statusConfig.color} />
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Current Status</p>
                    <p className={`text-lg font-bold ${statusConfig.color}`}>{statusConfig.label}</p>
                  </div>
                </div>
                {result.status_message && (
                  <p className="text-sm text-gray-700 mt-2">{result.status_message}</p>
                )}
              </div>

              {/* Progress Steps */}
              {!['rejected', 'withdrawn'].includes(result.status) && (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-4">Progress</p>
                  <div className="flex items-center justify-between">
                    {STEPS.map((step, i) => (
                      <div key={step.num} className="flex items-center">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            currentStep >= step.num
                              ? 'bg-blue-700 text-white'
                              : 'bg-gray-100 text-gray-400'
                          }`}>
                            {currentStep > step.num ? <CheckCircle size={16} weight="fill" /> : step.num}
                          </div>
                          <p className={`text-[10px] mt-1.5 text-center leading-tight ${currentStep >= step.num ? 'text-blue-700 font-medium' : 'text-gray-400'}`}>
                            {step.label}
                          </p>
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className={`w-6 sm:w-10 h-0.5 mx-1 ${currentStep > step.num ? 'bg-blue-700' : 'bg-gray-200'}`} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Application Info */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Application Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Reference</p>
                    <p className="text-sm font-semibold text-gray-900 font-mono">{result.reference_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Applicant</p>
                    <p className="text-sm text-gray-900">{result.full_name}</p>
                  </div>
                  {result.preferred_program && (
                    <div>
                      <p className="text-xs text-gray-400">Program</p>
                      <p className="text-sm text-gray-900">{result.preferred_program}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-400">Applied On</p>
                    <p className="text-sm text-gray-900">{new Date(result.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <p className="text-center text-xs text-gray-400">
                For questions about your application, contact us at <a href="mailto:hello@studyalliance.ca" className="text-blue-600 hover:underline">hello@studyalliance.ca</a>
              </p>
            </div>
          )}

          {/* No search yet */}
          {!result && !notFound && !loading && (
            <div className="text-center py-8 text-gray-400">
              <MagnifyingGlass size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Enter your reference number above to see your application status</p>
              <p className="text-xs mt-2">Reference numbers look like: SA-260418-0001</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
